/**
 * 008-reporting-dashboards T023 (US4, FR-015). Same shape as `../invoicing/GstSummary.tsx` and
 * `Variance.tsx`: a from/to-bounded, unpaged, client-exported report — period form driving
 * `searchParams`, fetch-on-submit-or-bookmarked-load, `ExportButton` fed rows mapped to
 * plainly-keyed objects in the exact header order (`flattenObject`'s `for...in` key-order hazard,
 * see `GstSummary.tsx`'s own comment).
 *
 * `GET /api/billing/reports/sales` takes `from`/`to`/`groupBy` (`brand|category|customer|sku|tier`,
 * default `brand`) — verified against `ReportController.GetSalesReport` and the running gateway
 * 2026-09-09. Grouping, totals and filters are all the API's (FR-015): this page sends the axis the
 * user picked and renders whatever rows came back, largest-`lineTotal`-first, exactly as ordered.
 *
 * Gated on `billing.read`, the code `GET /reports/sales` requires.
 */
import { useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { Button } from "rizzui";
import { WidgetCard, ErrorSurface, EmptyState, ExportButton, FieldError } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { DateField } from "../../components/date-field";
import { ListHeader } from "../../components/table/list-header";
import { ReportTable } from "../../components/table/table-view";
import { can } from "../../lib/permissions";
import { formatDate, sentenceCase, parseIsoDate, toIsoDate } from "../../lib/format";
import { getSalesReport, salesGroupingLabel, type SalesReport } from "../../lib/services/invoicing";
import type { AccountSummary, PermissionState } from "../../types";
import { salesColumns } from "./sales-columns";

const GROUPINGS = ["brand", "category", "customer", "sku", "tier"] as const;

/*
 * The rows render through `ReportTable` — paged in memory rather than `PagedTable` for the same
 * export-guard reason as `Variance.tsx`'s sections; the export maps every row, not the visible page.
 * `key={report.producedAt}` starts each fetch's table from its first page.
 */

export default function SalesReportPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<SalesReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const groupBy = searchParams.get("groupBy") ?? "brand";
  // Mirrors `from`/`to`, editable independently of the URL until "Run the report" commits it —
  // same split the search-param filter toolbars (orders/filters.tsx etc.) don't need, since those
  // apply on every change rather than on a separate submit.
  const [fromDate, setFromDate] = useState(from);
  const [toDate, setToDate] = useState(to);
  const [periodError, setPeriodError] = useState<string>();

  // Rules-of-hooks: called unconditionally, ahead of the permission-gate return below — same
  // reason GstSummary.tsx's own effect comes before its gate.
  useEffect(() => {
    if (from && to) run(from, to, groupBy);
  }, [from, to, groupBy]);

  if (!can(permissionState, "billing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403, detail: "The sales report is gated by billing.read, which your role does not hold." }} action="view the sales report" />;
  }

  async function run(nextFrom: string, nextTo: string, nextGroupBy: string) {
    setLoading(true);
    setError(null);
    try {
      const result = await getSalesReport({ from: nextFrom, to: nextTo, groupBy: nextGroupBy });
      setReport(result);
    } catch (err) {
      setError(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!fromDate || !toDate) return setPeriodError("Choose both a From and a To date.");
    setPeriodError(undefined);
    const form = new FormData(event.currentTarget);
    const nextGroupBy = String(form.get("groupBy") ?? "brand");
    setSearchParams({ from: fromDate, to: toDate, groupBy: nextGroupBy });
    run(fromDate, toDate, nextGroupBy);
  }

  const header = (
    <ListHeader
      title="Sales Report"
      description="Invoiced sales over a period, grouped by brand, category, customer, SKU or tier — the API’s own totals, never recomputed here."
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Reports", href: "/reports" }, { name: "Sales" }]}
    >
        {report && !report.empty ? (
          <ExportButton
            // Mapped in the EXACT displayed column order, not passed as SalesRow[] directly —
            // see GstSummary.tsx's own comment on flattenObject's for...in key-order hazard.
            data={report.rows.map((r) => ({
              Group: r.groupName || "",
              Invoices: r.invoiceCount,
              Quantity: r.quantity,
              "Taxable value": r.taxableValue,
              Tax: r.taxAmount,
              "Line total": r.lineTotal,
            }))}
            header="Group,Invoices,Quantity,Taxable value,Tax,Line total"
            fileName={`sales-${from}-to-${to}-by-${groupBy}`}
          />
        ) : null}
    </ListHeader>
  );

  const periodForm = (
    <WidgetCard title="Period and grouping" rounded="lg" headerClassName="border-muted border-b pb-4">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4 pt-4">
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-48"
          placeholderText="From…"
          selected={parseIsoDate(fromDate || null)}
          maxDate={parseIsoDate(toDate || null) ?? undefined}
          onChange={(date) => setFromDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "From", clearable: Boolean(fromDate), onClear: () => setFromDate("") }}
        />
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-48"
          placeholderText="To…"
          selected={parseIsoDate(toDate || null)}
          minDate={parseIsoDate(fromDate || null) ?? undefined}
          onChange={(date) => setToDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "To", clearable: Boolean(toDate), onClear: () => setToDate("") }}
        />
        <SelectField label="Group by" name="groupBy" defaultValue={groupBy} wrapperClassName="w-48">
          {GROUPINGS.map((g) => (
            <option key={g} value={g}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </option>
          ))}
        </SelectField>
        <Button type="submit" disabled={loading}>{loading ? "Running…" : "Run the report"}</Button>
        {periodError ? <div className="basis-full"><FieldError message={periodError} /></div> : null}
      </form>
    </WidgetCard>
  );

  if (!from || !to) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        {periodForm}
        <EmptyState title="Choose a period">Eight years of invoices are retained, so this report is always run against a bounded range rather than defaulting to all of them.</EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}
      {periodForm}

      {error ? <ErrorSurface error={error} action="run the sales report" /> : null}

      {report ? (
        report.empty ? (
          <WidgetCard
            title={<>{formatDate(from)} to {formatDate(to)} · by {salesGroupingLabel(report.groupBy)}</>}
            rounded="lg"
            headerClassName="border-muted border-b"
          >
            <EmptyState
              title="No invoices in this period"
              action={<Link to="/invoicing" className="text-primary text-sm underline">Open the invoice list</Link>}
            >
              {report.emptyReason ? sentenceCase(report.emptyReason) + "." : `No invoices were recorded between ${formatDate(from)} and ${formatDate(to)}.`}
            </EmptyState>
          </WidgetCard>
        ) : (
          <ReportTable<SalesReport["rows"][number]>
            key={report.producedAt}
            title={
              <>
                {formatDate(from)} to {formatDate(to)} · by {salesGroupingLabel(report.groupBy)} · {report.rows.length} {report.rows.length === 1 ? "row" : "rows"}
              </>
            }
            rows={report.rows}
            columns={salesColumns}
            getRowId={(row, index) => row.groupId ?? String(index)}
            caption="Sales for the chosen period, grouped and totalled by the API"
            searchPlaceholder="Search groups…"
          />
        )
      ) : null}
    </div>
  );
}
